import { Clipboard } from '@angular/cdk/clipboard';
import { HttpResponse } from '@angular/common/http';
import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';

import { Subscription } from 'rxjs';
import { AlcmeonContextService } from 'src/app/core/alcmeon-context/alcmeon-context.service';
import { ExternalAuthorizationsService } from 'src/app/parameters/external-authorizations/external-authorizations.service';
import { ExternalAuthorization } from 'src/app/parameters/external-authorizations/shared/external-authorization.model';
import { ConfirmDialogComponent } from 'src/app/shared/components/confirm-dialog/confirm-dialog.component';
import { Choice } from 'src/app/shared/components/select/choice.model';
import { BasicService } from 'src/app/shared/services/basic.service';
import { ApiConnectorHttpMethodEnum } from '../shared/api-connector-http-method.enum';
import { ApiConnector } from '../shared/api-connector.model';

@Component({
  selector: 'app-edit-api-connector',
  templateUrl: './edit-api-connector.component.html',
  styleUrls: ['./edit-api-connector.component.scss'],
})
export class EditApiConnectorComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() elementData: ApiConnector;
  @Output() hideDialog: EventEmitter<any> = new EventEmitter();
  @Output() cancelDialog: EventEmitter<any> = new EventEmitter();

  outgoingEmailsHref: string = this.getOutgoingEmailsUrl();
  invalidJsonBody: boolean;

  get responsesArray() {
    return this.form?.get('responses') as FormArray;
  }

  get requestFormGroup() {
    return this.form?.get('request') as FormGroup;
  }

  get headersArray() {
    return this.requestFormGroup?.get('headers') as FormArray;
  }
  externalAuthorizations: Choice[] = [];

  form = this.formBuilder.group({
    request: this.formBuilder.group({
      method: [ApiConnectorHttpMethodEnum.GET],
      url: ['', Validators.required],
      externalAuthorizationId: [null],
      headers: this.formBuilder.array([]),
      httpBody: [''],
    }),
    responses: this.formBuilder.array([]),
  });

  methodsItems = [
    { id: ApiConnectorHttpMethodEnum.GET, label: ApiConnectorHttpMethodEnum.GET },
    { id: ApiConnectorHttpMethodEnum.POST, label: ApiConnectorHttpMethodEnum.POST },
  ];
  MAX_ASSOCIATION_NUMBER = 10;

  subscriptions: Subscription = new Subscription();

  @ViewChild('templateRef') private templateRef: TemplateRef<any>;

  constructor(
    private alcmeonContextService: AlcmeonContextService,
    private basicService: BasicService,
    private formBuilder: FormBuilder,
    private dialog: MatDialog,
    private clipboard: Clipboard,
    private externalAuthorizationsService: ExternalAuthorizationsService
  ) {
    this.externalAuthorizationsService.findAll().subscribe({
      next: (response: HttpResponse<ExternalAuthorization[]>) => {
        const externalAuthorizations = response?.body || [];
        this.externalAuthorizations = externalAuthorizations.map(({ id, name }) => ({ id, label: name }));
        this.externalAuthorizations.unshift({ id: null, label: basicService.translate('none') });
      },
    });
  }

  ngOnInit() {
    this.subscriptions.add(
      this.requestFormGroup.get('method').valueChanges.subscribe(value => {
        if (value === ApiConnectorHttpMethodEnum.POST) {
          this.requestFormGroup.get('httpBody').addValidators(Validators.required);
          this.requestFormGroup.get('httpBody').enable();
        } else {
          this.requestFormGroup.get('httpBody').removeValidators(Validators.required);
          this.requestFormGroup.get('httpBody').disable();
        }
      })
    );

    this.subscriptions.add(
      this.requestFormGroup.get('httpBody').valueChanges.subscribe({
        next: value => {
          if (!value) {
            this.invalidJsonBody = null;
            return;
          }
          try {
            JSON.parse(value);
            this.invalidJsonBody = null;
          } catch (error) {
            this.invalidJsonBody = true;
          }
        },
      })
    );

    if (this.elementData) {
      if (this.elementData.responses?.length) {
        Array.from({ length: Math.min(this.elementData?.responses?.length, this.MAX_ASSOCIATION_NUMBER) }).forEach(() =>
          this.addAssociation()
        );
      }

      if (this.elementData.request?.headers?.length) {
        Array.from({ length: Math.max(this.elementData.request.headers.length, 1) }).forEach(() => this.addHeader());
      }

      this.form.patchValue(this.elementData);
    }
  }

  addHeader() {
    this.headersArray.push(
      this.formBuilder.group({
        name: [''],
        value: [''],
      })
    );
  }

  deleteHeader(index: number) {
    this.headersArray.removeAt(index);
  }

  addAssociation() {
    this.responsesArray.push(
      this.formBuilder.group({
        jsonPath: ['', Validators.required],
        variable: ['', Validators.required],
      })
    );
  }

  deleteAssociation(index: number) {
    this.responsesArray.removeAt(index);
  }

  copyCurlTestCommand() {
    const { method, url, headers, httpBody } = this.requestFormGroup.value;

    const headersPart = headers.reduce((sum, { name, value }) => (sum += ` -H "${name}: ${value}"`), '');
    const payload = method === ApiConnectorHttpMethodEnum.POST ? ` -d "${httpBody}" ` : '';
    const curlCommand = `curl -X ${method} ${payload} ${url} ${headersPart} --compressed`;

    this.clipboard.copy(curlCommand);
    this.basicService.toastInformation(
      'parameters/quick_replies/details/graph/edit_api_connector/copy_curl_test_command/success'
    );
  }

  ngAfterViewInit(): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      panelClass: ['large-size'],
      autoFocus: false,
      data: {
        title: this.basicService.translate('parameters/quick_replies/details/graph/edit_api_connector/title'),
        confirmBtnText: this.basicService.translate('save'),
        cancelBtnText: this.basicService.translate('cancel'),
        templateRef: this.templateRef,
        disable: () => this.form.invalid,
      },
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.hideDialog.emit({ type: 'ok', data: this.form.value });
      } else {
        this.cancelDialog.emit();
      }
    });
  }

  private getOutgoingEmailsUrl() {
    const path = `/#/${this.alcmeonContextService.companyId}/fuse/parameters/company-contacts`;
    return this.basicService.fullURLForRoutePath(path);
  }

  ngOnDestroy(): void {
    this.subscriptions?.unsubscribe();
  }
}
